'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var material = require('@mui/material');
var jsxRuntime = require('react/jsx-runtime');
var CloseIcon = require('@mui/icons-material/Close');
var React = require('react');
var InfoOutlinedIcon = require('@mui/icons-material/InfoOutlined');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var CloseIcon__default = /*#__PURE__*/_interopDefaultLegacy(CloseIcon);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var InfoOutlinedIcon__default = /*#__PURE__*/_interopDefaultLegacy(InfoOutlinedIcon);

const primary = {
    light: '#5B5B5B',
    main: '#323232',
    dark: '#000000',
};
const secondary = {
    light: '#EBF7FF',
    main: '#4A90E2',
    dark: '#3C7DCA',
};
const error = {
    light: '#FFB0B0',
    main: '#FF0000',
    dark: '#F10000',
};
const success = {
    light: '#7BC67E',
    main: '#4CAF50',
    dark: '#3B873E',
};
const fontFamily = '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif';
const headingsFontFamily = '"Distinct", "Helvetica Neue", "Helvetica", "Arial", sans-serif';
const palette = {
    primary,
    secondary,
    error,
    success,
    common: {
        black: '#000',
        white: '#fff',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
        primary: '#323232',
        secondary: '#ADADAD',
        tertiary: '#535050',
        disabled: 'rgba(0, 0, 0, 0.38)',
        hint: 'rgba(0, 0, 0, 0.38)',
    },
    divider: '#ADADAD',
    background: {
        default: '#FFFFFF',
        paper: '#F4F4F4',
    },
    action: {
        active: '#757575',
        hover: '#E5E5E5',
        hoverOpacity: 0.08,
        selected: '#EBF7FF',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: '#EAEAEA',
    },
};
const fontWeightLight = 300;
const fontWeightRegular = 400;
const fontWeightMedium = 500;
const fontWeightBold = 700;
const typography = {
    fontFamily,
    htmlFontSize: 16,
    fontSize: 16,
    fontWeightLight,
    fontWeightRegular,
    fontWeightMedium,
    fontWeightBold,
    h1: {
        fontFamily: headingsFontFamily,
        fontWeight: fontWeightBold,
        fontSize: `${32 / 16}rem`,
        lineHeight: 1.1,
        textTransform: 'uppercase',
        color: palette.text.primary,
    },
    h2: {
        fontFamily: headingsFontFamily,
        fontWeight: fontWeightBold,
        fontSize: `${24 / 16}rem`,
        lineHeight: 1.2,
        textTransform: 'capitalize',
        color: palette.text.primary,
    },
    h3: {
        fontFamily: headingsFontFamily,
        fontWeight: fontWeightBold,
        fontSize: `${24 / 16}rem`,
        lineHeight: 1.3,
        textTransform: 'capitalize',
        color: palette.text.primary,
    },
    h4: {
        fontFamily: headingsFontFamily,
        fontWeight: fontWeightBold,
        fontSize: `${20 / 16}rem`,
        lineHeight: 1.3,
        textTransform: 'capitalize',
        color: palette.text.primary,
    },
    h5: {
        fontWeight: fontWeightLight,
        fontStyle: 'italic',
        fontSize: `${20 / 16}rem`,
        lineHeight: 1.3,
        textTransform: 'capitalize',
        color: palette.text.primary,
    },
    subtitle1: {
        fontWeight: fontWeightMedium,
        fontSize: `${16 / 16}rem`,
        lineHeight: 1.5,
        color: palette.text.primary,
    },
    subtitle2: {
        fontWeight: fontWeightBold,
        fontSize: `${14 / 16}rem`,
        lineHeight: 1.43,
        color: palette.text.primary,
    },
    body1: {
        fontWeight: fontWeightRegular,
        fontSize: `${16 / 16}rem`,
        lineHeight: 1.5,
        color: palette.text.tertiary,
    },
    body2: {
        fontWeight: fontWeightRegular,
        fontSize: `${14 / 16}rem`,
        lineHeight: 1.43,
        color: palette.text.tertiary,
    },
    button: {
        fontWeight: fontWeightBold,
        fontSize: `${14 / 16}rem`,
        color: palette.text.primary,
    },
    overline: {
        fontWeight: fontWeightRegular,
        fontSize: `${12 / 16}rem`,
        lineHeight: 2.66,
        textTransform: 'uppercase',
        color: palette.text.primary,
    },
    caption: {
        fontWeight: fontWeightRegular,
        fontSize: `${12 / 16}rem`,
        lineHeight: 1.66,
        color: palette.text.tertiary,
    },
};
const spacing = 5;
const breakpoints = {
    xs: 0,
    sm: 480,
    md: 900,
    lg: 1200,
    xl: 1500,
};
const smUp = `@media (min-width:${breakpoints.sm}px)`;
const mdUp = `@media (min-width:${breakpoints.md}px)`;
const lgUp = `@media (min-width:${breakpoints.lg}px)`;
const xlUp = `@media (min-width:${breakpoints.xl}px)`;
const tenColumnsWide = (width) => {
    const gutter = 15;
    const column = (width - 11 * gutter) / 12;
    return 10 * column + 9 * gutter;
};
/**
 * The Phillips @material-ui theme
 */
const theme = material.createTheme({
    palette,
    typography,
    spacing,
    direction: 'ltr',
    breakpoints: {
        keys: ['xs', 'sm', 'md', 'lg', 'xl'],
        values: breakpoints,
    },
    mixins: {
        toolbar: {
            minHeight: 56,
            '@media (min-width:0px) and (orientation: landscape)': {
                minHeight: 48,
            },
            '@media (min-width:500px)': {
                minHeight: 64,
            },
        },
    },
    shape: {
        borderRadius: 0,
    },
    components: {
        MuiAccordion: {
            defaultProps: {
                square: true,
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: `${spacing}px 0px`,
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'primary',
            },
            styleOverrides: {
                root: {
                    marginTop: 0,
                    marginBottom: 0,
                },
            },
        },
        MuiFormControl: {
            defaultProps: {
                margin: 'normal',
            },
            styleOverrides: {
                marginNormal: {
                    marginTop: spacing * 2,
                    marginBottom: spacing,
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
        },
        MuiDialog: {
            defaultProps: {
                PaperProps: {
                    elevation: 2,
                },
                maxWidth: false,
            },
            styleOverrides: {
                // this accompanying maxWidth: false above creates the desired standard modal effect
                paperWidthFalse: {
                    maxWidth: `calc(100% - ${spacing * 6}px)`,
                },
                paperScrollPaper: {
                    maxHeight: `calc(100% - ${spacing * 12}px)`,
                    [smUp]: {
                        maxHeight: `calc(100% - ${spacing * 15}px)`,
                    },
                },
                paper: {
                    margin: 0,
                    marginTop: spacing * 3,
                    marginBottom: spacing * 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'flex-start',
                    width: '100%',
                    [smUp]: {
                        width: tenColumnsWide(breakpoints.sm),
                        paddingBottom: spacing * 9,
                    },
                    [mdUp]: {
                        width: tenColumnsWide(breakpoints.md),
                    },
                    [lgUp]: {
                        width: tenColumnsWide(breakpoints.lg),
                    },
                    [xlUp]: {
                        width: tenColumnsWide(breakpoints.xl),
                    },
                    paddingTop: 0,
                    paddingBottom: spacing * 6,
                    backgroundColor: palette.background.default,
                },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
            // FIXME: not a prop in mui v5?
            // disableTypography: true,
            },
            styleOverrides: {
                root: Object.assign(Object.assign({}, typography.h2), { textAlign: 'center' }),
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: palette.text.primary,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    paddingTop: spacing * 3,
                    paddingBottom: spacing * 3,
                },
            },
        },
        MuiDialogActions: {
            defaultProps: {
                disableSpacing: true,
            },
            styleOverrides: {
                root: {
                    marginLeft: spacing * 3,
                    marginRight: spacing * 3,
                    paddingTop: spacing * 3,
                    paddingBottom: spacing * 3,
                    flexDirection: 'column',
                    [smUp]: {
                        width: '50%',
                        margin: '0 auto',
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                variant: 'contained',
                color: 'primary',
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    padding: `0px ${spacing * 4}px`,
                    borderRadius: '100px',
                    minHeight: '40px',
                    minWidth: 'min(300px, 100%)',
                },
                outlined: {
                    borderWidth: '2px !important',
                    padding: `-1px ${spacing * 4 - 1}px`,
                },
                outlinedPrimary: {
                    borderColor: palette.primary.main,
                },
                text: {
                    padding: `0px ${spacing * 4}px`,
                },
                startIcon: {
                    marginLeft: 0,
                },
                endIcon: {
                    marginRight: 0,
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'always',
                color: 'secondary',
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                asterisk: {
                    display: 'none',
                },
                root: {
                    position: 'relative',
                    marginBottom: `${spacing}px`,
                    transform: 'none',
                    fontSize: typography.caption.fontSize,
                    fontWeight: fontWeightBold,
                    color: palette.text.tertiary,
                    '&.Mui-required': {
                        '&:after': {
                            display: 'none',
                        },
                    },
                    '&:after': {
                        content: '"(Optional)"',
                        position: 'relative',
                        top: 0,
                        left: 8,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: `${spacing}px`,
                    padding: `${spacing * 2}px`,
                },
                notchedOutline: {
                    top: 0,
                    '& legend': {
                        display: 'none',
                    },
                },
                input: {
                    borderRadius: `${spacing}px`,
                    fontSize: typography.body2.fontSize,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    padding: 0,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                input: {
                    padding: '0 !important',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    input: {
                        padding: 0,
                    },
                    sx: {
                        mx: `${spacing}px`,
                        height: '40px',
                    },
                },
            },
            defaultProps: {
                InputLabelProps: {
                    shrink: true,
                },
            },
        },
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: palette.background.default,
                    color: 'inherit',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    paddingLeft: 0,
                    paddingRight: 0,
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    paddingLeft: 0,
                    paddingRight: 0,
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                indicatorColor: 'primary',
            },
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${palette.divider}`,
                },
                indicator: {
                    height: 4,
                    borderRadius: 10,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                textColorInherit: {
                    opacity: 0.5,
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 200,
            },
        },
        MuiTypography: {
            styleOverrides: {
                gutterBottom: {
                    marginBottom: '0.7em',
                },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    padding: spacing,
                    margin: '0 -8px',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&$disabled': {
                        WebkitTextFillColor: palette.text.disabled, // Fix opacity Safari bug
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&:focus': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
            },
        },
    },
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const Dialog = (_a) => {
    var { open, onClose, children } = _a, otherProps = __rest(_a, ["open", "onClose", "children"]);
    return (jsxRuntime.jsxs(material.Dialog, Object.assign({ open: open, onClose: onClose }, otherProps, { children: [jsxRuntime.jsx(material.IconButton, Object.assign({ sx: {
                    alignSelf: 'flex-end',
                    borderRadius: '100%',
                    minWidth: 0,
                    p: 1.5,
                    mt: 1.5,
                    mr: 1.5,
                }, onClick: () => onClose === null || onClose === void 0 ? void 0 : onClose({}, 'backdropClick') }, { children: jsxRuntime.jsx(CloseIcon__default['default'], {}) })), jsxRuntime.jsx(material.Grid, Object.assign({ container: true, justifyContent: "center" }, { children: jsxRuntime.jsx(material.Grid, Object.assign({ item: true, xs: 12, sm: 8 }, { children: children })) }))] })));
};

const TooltipInfoIcon = React__default['default'].forwardRef((_a, ref) => {
    var { sx } = _a, props = __rest(_a, ["sx"]);
    return (jsxRuntime.jsx(InfoOutlinedIcon__default['default'], Object.assign({ ref: ref, sx: [
            {
                color: 'secondary.main',
                '&:hover': {
                    color: 'secondary.dark',
                },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ], fontSize: "inherit" }, props)));
});

var noop = function () { };
var isBrowser = typeof window !== 'undefined';

var useIsomorphicLayoutEffect = isBrowser ? React.useLayoutEffect : React.useEffect;
var useIsomorphicLayoutEffect$1 = useIsomorphicLayoutEffect;

var defaultState = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
};
function useMeasure() {
    var _a = React.useState(null), element = _a[0], ref = _a[1];
    var _b = React.useState(defaultState), rect = _b[0], setRect = _b[1];
    var observer = React.useMemo(function () {
        return new window.ResizeObserver(function (entries) {
            if (entries[0]) {
                var _a = entries[0].contentRect, x = _a.x, y = _a.y, width = _a.width, height = _a.height, top_1 = _a.top, left = _a.left, bottom = _a.bottom, right = _a.right;
                setRect({ x: x, y: y, width: width, height: height, top: top_1, left: left, bottom: bottom, right: right });
            }
        });
    }, []);
    useIsomorphicLayoutEffect$1(function () {
        if (!element)
            return;
        observer.observe(element);
        return function () {
            observer.disconnect();
        };
    }, [element]);
    return [ref, rect];
}
var useMeasure$1 = isBrowser && typeof window.ResizeObserver !== 'undefined'
    ? useMeasure
    : (function () { return [noop, defaultState]; });

const isValidUrl = (url) => {
    const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+,]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(url);
};

const getTransformedUrl = ({ imageUrl, crop = 'fill', gravity, width, height, roundTo = 1, }) => {
    if (imageUrl === undefined) {
        return undefined;
    }
    // annoyingly I am handed an invalid url with transformation that I need to clean
    const cleanedUrl = imageUrl.replace('@height', '').replace('@width', '');
    if (!isValidUrl(cleanedUrl))
        return imageUrl;
    if (new URL(imageUrl).hostname !== 'assets.phillips.com')
        return imageUrl;
    const splitUrl = imageUrl.split(/(image\/upload\/)/);
    if (splitUrl.length > 1) {
        const root = splitUrl.slice(0, 2).join('');
        // remove any existing transformations
        const imageId = splitUrl[2].split(/([a-z]_[a-z@0-9]+)(,[a-z]_[a-z@0-9]+)*\//).reverse()[0];
        const transformations = [];
        transformations.push(`c_${crop}`);
        if (gravity !== undefined) {
            transformations.push(`g_${gravity}`);
        }
        if (width !== undefined && width !== 0) {
            transformations.push(`w_${Math.ceil(width / roundTo) * roundTo}`);
        }
        if (height !== undefined && height !== 0) {
            transformations.push(`h_${Math.ceil(height / roundTo) * roundTo}`);
        }
        return `${root}${transformations.length > 0 ? `${transformations.join(',')}/` : ``}${imageId}`;
    }
    return imageUrl;
};

const preloadImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgEAEAABkAFaLhbucAAAAASUVORK5CYII=';
const useCloudinaryImage = (_a) => {
    var transformationProps = __rest(_a, []);
    const maximumDimensionsRequested = React.useRef({ width: 0, height: 0, ratio: 0 });
    const [transformedImageUrl, setTransformedImageUrl] = React.useState(preloadImage);
    const [imageRef, { width, height }] = useMeasure$1();
    const ratio = React.useMemo(() => width / height, [width, height]);
    React.useEffect(() => {
        if (width === 0 || height === 0) {
            return () => null;
        }
        if (maximumDimensionsRequested.current.width >= width &&
            maximumDimensionsRequested.current.height >= height &&
            maximumDimensionsRequested.current.ratio === ratio) {
            return () => null;
        }
        const transformedUrl = getTransformedUrl(Object.assign(Object.assign({}, transformationProps), { width, height }));
        if (transformedUrl === undefined) {
            return () => null;
        }
        const img = new Image();
        img.onload = () => {
            maximumDimensionsRequested.current.width = width;
            maximumDimensionsRequested.current.height = height;
            maximumDimensionsRequested.current.ratio = ratio;
            setTransformedImageUrl(transformedUrl);
        };
        img.src = transformedUrl;
        return () => {
            img.onload = null;
        };
    }, [transformationProps, width, height, ratio]);
    return { imageRef, transformedImageUrl };
};

/* eslint-disable import/prefer-default-export */

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useCloudinaryImage: useCloudinaryImage
});

exports.Dialog = Dialog;
exports.TooltipInfoIcon = TooltipInfoIcon;
exports.hooks = index;
exports.theme = theme;
